import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getErrorMessage } from '../lib/api';
import api, { uploadApi } from '../lib/api';
import { compressImage } from '../utils/compressImage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
    PawPrint, Mail, Lock, User, Phone, MapPin, Loader2, 
    Home, Footprints, Car, FileText, Shield, ChevronRight, ChevronLeft, AlertCircle, CheckCircle2, CheckCircle, Eye, EyeOff,
    Building2, Camera, Upload
} from 'lucide-react';
import { USER_TYPES, SERVICE_TYPES, validateCPF, maskCPF } from '../lib/utils';
import Logo from '../components/Logo';
import VehicleStep from '../components/VehicleStep';
import { ux } from '../utils/uxMessages';

const userTypeIcons = {
    tutor: PawPrint,
    host: Home,
    caregiver: Footprints,
    driver: Car,
    partner: Building2,
};

const userTypeLabels = {
    tutor: { label: 'Tutor', description: 'Tenho pets e busco cuidadores' },
    host: { label: 'Hospedagem', description: 'Ofereço hospedagem para pets' },
    caregiver: { label: 'Passeador/Day Care', description: 'Ofereço passeios ou day care' },
    driver: { label: 'Táxi Dog', description: 'Faço transporte de pets' },
    partner: { label: 'Parceria', description: 'Veterinário, Petshop, etc.' },
};

const STATES = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 
    'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 
    'SP', 'SE', 'TO'
];

export default function Register() {
    const navigate = useNavigate();
    const location = useLocation();
    const { register, logout, user: currentUser, updateUser } = useAuth();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [loadingStage, setLoadingStage] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successRedirect, setSuccessRedirect] = useState('/provider');
    const [successType, setSuccessType] = useState('provider'); // 'provider' | 'verification'
    const [step, setStep] = useState(1);
    const [loadingCep, setLoadingCep] = useState(false);
    const [cpfError, setCpfError] = useState('');
    const [cpfValid, setCpfValid] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    // Verificar se veio do Google Login (via URL param OU location.state)
    const fromGoogle = location.state?.fromGoogle || searchParams.get('from_google') === 'true';
    const googleUser = location.state?.googleUser || null;
    const isGoogleProvider = fromGoogle && !location.state?.tutorMode && ['host', 'caregiver', 'driver', 'partner'].includes(location.state?.googleUser?.user_type);
    
    // Google Tutor: entra no funil completo de cadastro
    const isGoogleTutor = fromGoogle && !isGoogleProvider;
    
    // Check if this is an upgrade from tutor to provider
    const isUpgrade = searchParams.get('upgrade') === 'true';
    const upgradeType = searchParams.get('type');
    const fromTutor = searchParams.get('from') === 'tutor';
    
    // Limpa qualquer sessão anterior ao entrar na página de registro (only if not upgrading and not from Google)
    useEffect(() => {
        if (!isUpgrade && !fromGoogle) {
            // Limpeza completa: localStorage + cookies + estado React
            localStorage.removeItem('receppet_token');
            localStorage.removeItem('receppet_user');
            localStorage.removeItem('receppet_active_panel');
            try {
                document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + window.location.hostname;
            } catch (e) {}
            logout();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Google Tutor: pular step 1 (seleção de tipo), ir direto para step 2 (info pessoal)
    useEffect(() => {
        if (isGoogleTutor) {
            setFormData(prev => ({
                ...prev,
                name: googleUser?.name || currentUser?.name || prev.name,
                email: googleUser?.email || currentUser?.email || prev.email,
                profile_photo: googleUser?.profile_photo || currentUser?.profile_photo || prev.profile_photo,
                user_type: 'tutor',
            }));
            setStep(2);
        }
    }, [isGoogleTutor]); // eslint-disable-line react-hooks/exhaustive-deps
    
    const [formData, setFormData] = useState({
        // Basic - pré-preencher com dados do Google se disponível
        name: googleUser?.name || '',
        email: googleUser?.email || '',
        phone: '',
        password: '',
        confirmPassword: '',
        user_type: upgradeType || (searchParams.get('type') === 'provider' ? 'host' : 'tutor'),
        profile_photo: googleUser?.profile_photo || '',
        // Address
        address: {
            cep: '',
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: '',
        },
        // Emergency Contact
        emergency_contact: {
            name: '',
            phone: '',
            relationship: '',
        },
        // Provider fields
        cpf: '',
        identity_document: '',
        experience_description: '',
        services_offered: [],
        profile_photo: null, // Photo file
        profile_photo_preview: '', // Preview URL
        // Banking/PIX fields
        pix_key_type: '',
        pix_key: '',
        bank_name: '',
        bank_account_type: '',
        bank_account_holder: '',
        // Vehicle (Taxi Dog)
        vehicle_data: {
            vehicle_type: '', vehicle_brand: '', vehicle_model: '',
            vehicle_year: '', vehicle_plate: '', vehicle_color: '',
            cnh_number: '', cnh_category: '', cnh_expiry: '',
            vehicle_photos: { front: '', back: '', left: '', right: '' },
            cnh_photo_url: '',
            has_air_conditioning: false, has_pet_seat: false,
            has_pet_crate: false, max_pet_weight: '',
        },
        // Terms
        accepted_terms: false,
    });
    
    // Pre-fill form data if upgrading from tutor
    useEffect(() => {
        if (isUpgrade && currentUser) {
            setFormData(prev => ({
                ...prev,
                name: currentUser.name || '',
                email: currentUser.email || '',
                phone: currentUser.phone || '',
                user_type: upgradeType || 'host',
                cpf: currentUser.cpf || '',
                address: {
                    cep: currentUser.cep || '',
                    street: currentUser.street || '',
                    number: currentUser.number || '',
                    complement: currentUser.complement || '',
                    neighborhood: currentUser.neighborhood || '',
                    city: currentUser.city || '',
                    state: currentUser.state || '',
                },
                // Skip password for upgrade - will use existing
                password: 'UPGRADE_NO_CHANGE',
                confirmPassword: 'UPGRADE_NO_CHANGE',
            }));
            
            // If CPF is already filled, validate it
            if (currentUser.cpf) {
                const cleanCPF = currentUser.cpf.replace(/\D/g, '');
                if (cleanCPF.length === 11 && validateCPF(cleanCPF)) {
                    setCpfValid(true);
                }
            }
            
            // Skip to step 2 (provider info) since basic info is pre-filled
            setStep(2);
        }
    }, [isUpgrade, currentUser, upgradeType]);

    // Google Provider: pular identificação pessoal, ir direto para dados complementares
    useEffect(() => {
        if (isGoogleProvider) {
            // Pré-selecionar tipo de prestador padrão (host) se não veio setado
            setFormData(prev => ({
                ...prev,
                name: googleUser?.name || prev.name,
                email: googleUser?.email || prev.email,
                profile_photo: googleUser?.profile_photo || prev.profile_photo,
                user_type: prev.user_type === 'tutor' ? 'host' : prev.user_type,
                password: 'GOOGLE_NO_PASSWORD',
                confirmPassword: 'GOOGLE_NO_PASSWORD',
            }));
            // Ir direto para step 1 (seleção de tipo de prestador) e de lá pular para step 3
            setStep(1);
        }
    }, [isGoogleProvider]);

    const isProvider = ['host', 'caregiver', 'driver', 'partner'].includes(formData.user_type);
    const wantsTaxiDog = isProvider && formData.services_offered?.includes('taxi_dog');
    const totalSteps = isProvider ? (wantsTaxiDog ? 5 : 4) : 3;

    // CPF validation on change
    const handleCpfChange = (value) => {
        const maskedValue = maskCPF(value);
        setFormData(prev => ({ ...prev, cpf: maskedValue }));
        
        const cleanCPF = value.replace(/\D/g, '');
        if (cleanCPF.length === 11) {
            if (validateCPF(cleanCPF)) {
                setCpfValid(true);
                setCpfError('');
            } else {
                setCpfValid(false);
                setCpfError('CPF inválido. Verifique os números digitados.');
            }
        } else if (cleanCPF.length > 0) {
            setCpfValid(false);
            setCpfError('');
        } else {
            setCpfValid(false);
            setCpfError('');
        }
    };

    const handleCepLookup = async (cep) => {
        const cleanCep = cep.replace(/\D/g, '');
        if (cleanCep.length !== 8) {
            toast.error('CEP deve ter 8 dígitos');
            return;
        }
        
        setLoadingCep(true);
        try {
            const response = await api.get(`/cep/${cleanCep}`);
            if (response.data && response.data.street) {
                setFormData({
                    ...formData,
                    address: {
                        ...formData.address,
                        cep: cleanCep,
                        street: response.data.street || '',
                        neighborhood: response.data.neighborhood || '',
                        city: response.data.city || '',
                        state: response.data.state || '',
                    }
                });
                toast.success('Endereço encontrado!');
            } else {
                toast.error('CEP não encontrado ou inválido');
            }
        } catch (error) {
            toast.error(getErrorMessage(error, 'Erro ao buscar CEP. Tente novamente.'));
        } finally {
            setLoadingCep(false);
        }
    };

    const validateStep = (currentStep) => {
        switch (currentStep) {
            case 1:
                return true; // User type selection
            case 2:
                if (!formData.name || !formData.email || !formData.phone) {
                    toast.error('Preencha todos os campos obrigatórios');
                    return false;
                }
                // Skip password validation ONLY for upgrade (password already exists)
                if (!isUpgrade) {
                    if (!formData.password) {
                        toast.error('Preencha a senha');
                        return false;
                    }
                    if (formData.password !== formData.confirmPassword) {
                        toast.error('As senhas não coincidem');
                        return false;
                    }
                    if (formData.password.length < 6) {
                        toast.error('A senha deve ter pelo menos 6 caracteres');
                        return false;
                    }
                }
                return true;
            case 3:
                if (!formData.address.cep || !formData.address.street || !formData.address.number || 
                    !formData.address.neighborhood || !formData.address.city || !formData.address.state) {
                    toast.error('Preencha o endereço completo');
                    return false;
                }
                // Google provider: phone é obrigatório aqui (pulou step 2)
                if (isGoogleProvider && !formData.phone) {
                    toast.error('Telefone é obrigatório');
                    return false;
                }
                return true;
            case 4:
                if (isProvider) {
                    if (!formData.cpf) {
                        toast.error('CPF é obrigatório');
                        return false;
                    }
                    // Validate CPF format
                    const cleanCPF = formData.cpf.replace(/\D/g, '');
                    if (cleanCPF.length !== 11 || !validateCPF(cleanCPF)) {
                        toast.error('CPF inválido. Verifique os números digitados.');
                        return false;
                    }
                    if (!formData.experience_description) {
                        toast.error('Descrição de experiência é obrigatória');
                        return false;
                    }
                    // Validate PIX/Bank info
                    if (!formData.pix_key_type) {
                        toast.error('Selecione o tipo de chave PIX');
                        return false;
                    }
                    if (!formData.pix_key) {
                        toast.error('Informe sua chave PIX para recebimentos');
                        return false;
                    }
                    if (!formData.bank_account_holder) {
                        toast.error('Informe o nome do titular da conta');
                        return false;
                    }
                    if (!formData.profile_photo) {
                        toast.error('Envie uma foto de perfil para seu cadastro');
                        return false;
                    }
                }
                return true;
            case 5:
                // Vehicle validation (only for providers with taxi_dog)
                if (wantsTaxiDog) {
                    const v = formData.vehicle_data;
                    if (!v.vehicle_type || !v.vehicle_brand || !v.vehicle_model || !v.vehicle_plate || !v.vehicle_color) {
                        toast.error('Preencha todos os dados do veículo');
                        return false;
                    }
                    if (!v.cnh_number || !v.cnh_category || !v.cnh_expiry) {
                        toast.error('Preencha todos os dados da CNH');
                        return false;
                    }
                    const photos = v.vehicle_photos || {};
                    if (!photos.front || !photos.back || !photos.left || !photos.right) {
                        toast.error('Envie as 4 fotos obrigatórias do veículo (Frente, Traseira, Esquerda, Direita)');
                        return false;
                    }
                    if (!v.cnh_photo_url) {
                        toast.error('Envie a foto da CNH');
                        return false;
                    }
                    if (!v.crlv_url) {
                        toast.error('Envie a foto do CRLV (documento do veiculo)');
                        return false;
                    }
                }
                return true;
            default:
                return true;
        }
    };

    const nextStep = () => {
        if (validateStep(step)) {
            setStep(step + 1);
        }
    };

    const prevStep = () => {
        setStep(step - 1);
    };

    // Função especial para usuários Google que escolhem TUTOR
    const handleGoogleTutorActivation = async () => {
        if (!fromGoogle || formData.user_type !== 'tutor') return;
        
        setLoading(true);
        try {
            // Atualizar perfil do usuário Google para Tutor
            const response = await api.put('/users/me', {
                user_type: 'tutor',
                profile_completed: true,
                approval_status: 'approved',
                roles: ['tutor']
            });
            
            // Atualizar contexto
            updateUser(response.data);
            localStorage.setItem('receppet_user', JSON.stringify(response.data));
            
            toast.success('Perfil de Tutor ativado com sucesso!');
            navigate('/tutor', { replace: true });
        } catch (error) {
            toast.error(getErrorMessage(error, 'Erro ao ativar perfil'));
            setLoading(false);
        }
    };

    // Handler para o botão Continuar no Step 1
    const handleStep1Continue = () => {
        // Google Provider: pular step 2 (identificação pessoal), ir para step 3 (dados complementares)
        if (isGoogleProvider && isProvider) {
            setStep(3);
            return;
        }
        
        // Caso contrário, seguir para próximo step
        nextStep();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.accepted_terms) {
            toast.error('Você precisa aceitar os Termos de Uso e Política de Privacidade');
            return;
        }

        setLoading(true);
        setLoadingStage('Preparando cadastro...');
        try {
            const { confirmPassword, profile_photo, profile_photo_preview, ...registerData } = formData;
            
            // Upload photo first if present (using public endpoint for registration)
            let profilePhotoUrl = null;
            if (profile_photo && isProvider) {
                setLoadingStage('Enviando foto de perfil...');
                const compressed = await compressImage(profile_photo, { maxWidth: 800, maxHeight: 800, quality: 0.75 });
                const photoFormData = new FormData();
                photoFormData.append('file', compressed);
                
                const uploadResponse = await uploadApi.post('/upload/image/public', photoFormData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                profilePhotoUrl = uploadResponse.data.url;
            }
            
            setLoadingStage('Finalizando cadastro...');
            
            // GOOGLE TUTOR: Usar endpoint UPSERT (atualiza perfil existente, não cria novo)
            if (isGoogleTutor) {
                const response = await api.post('/auth/complete-google-profile', {
                    phone: formData.phone,
                    password: formData.password,
                    address: formData.address,
                    emergency_contact: formData.emergency_contact?.name ? formData.emergency_contact : null,
                    accepted_terms: true,
                });
                
                const { user: updatedUser, token: newToken } = response.data;
                if (newToken) {
                    localStorage.setItem('receppet_token', newToken);
                }
                if (updatedUser) {
                    updateUser(updatedUser);
                    localStorage.setItem('receppet_user', JSON.stringify(updatedUser));
                }
                
                toast.success('Cadastro realizado! Verifique seu telefone.');
                navigate('/verification', { replace: true });
                return;
            }
            
            // GOOGLE PROVIDER: Usar endpoint dedicado
            if (isGoogleProvider && isProvider) {
                const providerData = {
                    provider_type: formData.user_type,
                    phone: formData.phone,
                    address: formData.address,
                    cpf: formData.cpf,
                    experience_description: formData.experience_description,
                    services_offered: formData.services_offered,
                    identity_document: formData.identity_document,
                    pix_key_type: formData.pix_key_type,
                    pix_key: formData.pix_key,
                    bank_name: formData.bank_name,
                    bank_account_holder: formData.bank_account_holder,
                    profile_photo: profilePhotoUrl,
                    emergency_contact: formData.emergency_contact?.name ? formData.emergency_contact : null,
                    accepted_terms: true,
                };
                
                const response = await api.post('/auth/complete-provider-registration', providerData);
                const { user: updatedUser, token: newToken } = response.data;
                
                // Atualizar token e contexto
                if (newToken) {
                    localStorage.setItem('receppet_token', newToken);
                }
                updateUser(updatedUser);
                localStorage.setItem('receppet_user', JSON.stringify(updatedUser));
                
                toast.success('Cadastro enviado para aprovação!');
                setSuccessType('provider');
                setSuccessRedirect('/provider');
                setShowSuccessModal(true);
                return;
            }
            
            // Prepare data for normal registration
            const submitData = {
                ...registerData,
                profile_photo: profilePhotoUrl,
                services_offered: isProvider ? formData.services_offered : undefined,
            };
            
            // Include vehicle data if taxi_dog provider
            if (wantsTaxiDog && formData.vehicle_data) {
                setLoadingStage('Salvando dados do veículo...');
                const v = formData.vehicle_data;
                submitData.vehicle_data = {
                    vehicle_type: v.vehicle_type,
                    vehicle_brand: v.vehicle_brand,
                    vehicle_model: v.vehicle_model,
                    vehicle_year: v.vehicle_year,
                    vehicle_plate: v.vehicle_plate,
                    vehicle_color: v.vehicle_color,
                    cnh_number: v.cnh_number,
                    cnh_category: v.cnh_category,
                    cnh_expiry: v.cnh_expiry,
                    vehicle_photos: v.vehicle_photos,
                    cnh_photo_url: v.cnh_photo_url,
                    crlv_url: v.crlv_url,
                    has_air_conditioning: v.has_air_conditioning,
                    has_pet_seat: v.has_pet_seat,
                    has_pet_crate: v.has_pet_crate,
                    max_pet_weight: v.max_pet_weight ? parseInt(v.max_pet_weight) : null,
                };
            }
            
            // If upgrading, send the user ID header and use API directly
            if (isUpgrade && currentUser) {
                const response = await api.post('/auth/register', submitData, {
                    headers: {
                        'X-Upgrade-User-Id': currentUser.id
                    }
                });
                
                // Update local storage with new token
                if (response.data.token) {
                    localStorage.setItem('token', response.data.token);
                    localStorage.setItem('receppet_user', JSON.stringify(response.data.user));
                }
                
                toast.success(response.data.message || 'Conta atualizada com sucesso!');
                
                // Redirect to provider dashboard
                const dashboardRoute = submitData.user_type === 'host' ? '/host' : 
                                       submitData.user_type === 'driver' ? '/driver' : '/provider';
                setSuccessType('provider');
                setSuccessRedirect(dashboardRoute);
                setShowSuccessModal(true);
                return;
            }
            
            setLoadingStage('Criando sua conta...');
            const result = await register(submitData);
            
            if (isProvider) {
                // Providers go for approval after verification
                setSuccessType('verification');
                setSuccessRedirect('/verification');
                setShowSuccessModal(true);
            } else {
                // Tutors go directly to verification
                toast.success('Cadastro realizado! Verifique seu e-mail e telefone.');
                navigate('/verification');
            }
        } catch (error) {
            toast.error(getErrorMessage(error, 'Erro ao cadastrar'));
        } finally {
            setLoading(false);
            setLoadingStage('');
        }
    };

    const toggleService = (service) => {
        const current = formData.services_offered || [];
        const updated = current.includes(service)
            ? current.filter(s => s !== service)
            : [...current, service];
        setFormData({ ...formData, services_offered: updated });
    };

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const handleGoogleLogin = () => {
        const redirectUrl = window.location.origin + '/auth/callback';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen hero-gradient flex items-center justify-center p-4 py-8" data-testid="register-page">
            <div className="w-full max-w-xl">
                {/* Back to login link */}
                <div className="mb-4">
                    <button
                        onClick={() => navigate('/login')}
                        className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
                        data-testid="register-back-btn"
                    >
                        <ChevronLeft className="w-5 h-5" />
                        <span className="text-sm font-medium">Voltar ao login</span>
                    </button>
                </div>
            <Card className="w-full shadow-xl border-0">
                <CardHeader className="text-center pb-2">
                    <div className="flex justify-center mb-4">
                        <Logo size="lg" />
                    </div>
                    <CardTitle className="text-2xl font-bold font-['Nunito']">Criar conta</CardTitle>
                    <CardDescription>
                        {step === 1 && (isGoogleProvider ? 'Tipo de serviço' : 'Escolha seu tipo de perfil')}
                        {step === 2 && 'Informações pessoais'}
                        {step === 3 && (isGoogleProvider ? 'Telefone e Endereço' : 'Endereço')}
                        {step === 4 && isProvider && 'Informações profissionais'}
                        {step === 5 && wantsTaxiDog && 'Dados do Veículo'}
                        {step === totalSteps + 1 && 'Termos e Condições'}
                    </CardDescription>
                    
                    {/* Progress indicator */}
                    <div className="flex justify-center gap-2 mt-4">
                        {[...Array(totalSteps)].map((_, i) => (
                            <div 
                                key={i}
                                className={`h-2 rounded-full transition-all ${
                                    i + 1 <= step ? 'w-8 bg-[#4A90B2]' : 'w-2 bg-slate-200'
                                }`}
                            />
                        ))}
                    </div>
                </CardHeader>
                
                <CardContent>
                    <form onSubmit={handleSubmit}>
                        {/* Step 1: User Type */}
                        {step === 1 && (
                            <div className="space-y-4">
                                {/* Mensagem se veio do Google como Prestador */}
                                {isGoogleProvider && (
                                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                                        <p className="text-sm text-blue-800">
                                            <strong>Logado como:</strong> {googleUser?.email || currentUser?.email}
                                        </p>
                                        <p className="text-xs text-blue-600 mt-1">
                                            Escolha o tipo de serviço que deseja oferecer
                                        </p>
                                    </div>
                                )}

                                <RadioGroup
                                    value={formData.user_type}
                                    onValueChange={(value) => setFormData({ ...formData, user_type: value })}
                                    className="grid grid-cols-2 gap-4"
                                >
                                    {Object.entries(userTypeLabels)
                                        .filter(([key]) => isGoogleProvider ? key !== 'tutor' : true)
                                        .map(([key, { label, description }]) => {
                                        const Icon = userTypeIcons[key];
                                        if (!Icon) return null;
                                        return (
                                            <Label
                                                key={key}
                                                htmlFor={key}
                                                className={`flex flex-col items-center p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                                                    formData.user_type === key
                                                        ? 'border-[#4A90B2] bg-[#F0F9FF]'
                                                        : 'border-slate-200 hover:border-slate-300'
                                                }`}
                                                data-testid={`user-type-${key}`}
                                            >
                                                <RadioGroupItem value={key} id={key} className="sr-only" />
                                                <Icon className={`w-8 h-8 mb-2 ${formData.user_type === key ? 'text-[#4A90B2]' : 'text-slate-400'}`} />
                                                <span className="font-semibold text-sm">{label}</span>
                                                <span className="text-xs text-slate-500 text-center mt-1">{description}</span>
                                            </Label>
                                        );
                                    })}
                                </RadioGroup>

                                {/* Google Login Option - esconder se já veio do Google */}
                                {!fromGoogle && (
                                    <>
                                        <div className="relative my-6">
                                            <div className="absolute inset-0 flex items-center">
                                                <span className="w-full border-t border-slate-200" />
                                            </div>
                                            <div className="relative flex justify-center text-xs uppercase">
                                                <span className="bg-white px-2 text-slate-500">ou continue com</span>
                                            </div>
                                        </div>

                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full"
                                            onClick={handleGoogleLogin}
                                            data-testid="google-login-btn"
                                        >
                                            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                            </svg>
                                            Entrar com Google
                                        </Button>
                                    </>
                                )}

                                {/* Mensagem se veio do Google */}
                                {fromGoogle && (
                                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl mt-4">
                                        <p className="text-sm text-blue-800">
                                            <strong>Logado como:</strong> {googleUser?.email}
                                        </p>
                                        <p className="text-xs text-blue-600 mt-1">
                                            Escolha seu tipo de perfil para continuar
                                        </p>
                                    </div>
                                )}

                                <Button
                                    type="button"
                                    onClick={handleStep1Continue}
                                    className="w-full btn-primary"
                                    disabled={loading}
                                    data-testid="next-step-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Ativando...
                                        </>
                                    ) : fromGoogle && formData.user_type === 'tutor' ? (
                                        <>
                                            Ativar como Tutor
                                            <ChevronRight className="w-4 h-4 ml-2" />
                                        </>
                                    ) : (
                                        <>
                                            Continuar
                                            <ChevronRight className="w-4 h-4 ml-2" />
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}

                        {/* Step 2: Personal Info */}
                        {step === 2 && (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Nome completo *</Label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="name"
                                            placeholder="Seu nome completo"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className={`pl-10 ${isGoogleTutor ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : 'bg-white'}`}
                                            readOnly={isGoogleTutor}
                                            required
                                            data-testid="register-name"
                                        />
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="email">E-mail *</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="seu@email.com"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className={`pl-10 ${isGoogleTutor ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : 'bg-white'}`}
                                            readOnly={isGoogleTutor}
                                            required
                                            data-testid="register-email"
                                        />
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="phone">Telefone *</Label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="phone"
                                            placeholder="(11) 99999-9999"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            className="pl-10 bg-white"
                                            required
                                            data-testid="register-phone"
                                        />
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="password">Senha *</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="password"
                                            type={showPassword ? "text" : "password"}
                                            placeholder="Mínimo 6 caracteres"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            className="pl-10 pr-10 bg-white"
                                            required
                                            data-testid="register-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirmar senha *</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="confirmPassword"
                                            type={showConfirmPassword ? "text" : "password"}
                                            placeholder="Confirme sua senha"
                                            value={formData.confirmPassword}
                                            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                            className="pl-10 pr-10 bg-white"
                                            required
                                            data-testid="register-confirm-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>

                                <div className="flex gap-3 pt-2">
                                    <Button type="button" variant="outline" onClick={() => isGoogleTutor ? navigate('/login') : prevStep()} className="flex-1" data-testid="back-step-btn">
                                        <ChevronLeft className="w-4 h-4 mr-2" />
                                        Voltar
                                    </Button>
                                    <Button type="button" onClick={nextStep} className="flex-1 btn-primary" data-testid="next-step-btn">
                                        Continuar
                                        <ChevronRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Address */}
                        {step === 3 && (
                            <div className="space-y-4">
                                {/* Telefone - mostrar aqui para usuários Google (pularam step 2) */}
                                {isGoogleProvider && (
                                    <div className="space-y-2">
                                        <Label htmlFor="phone">Telefone *</Label>
                                        <div className="relative">
                                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <Input
                                                id="phone"
                                                placeholder="(11) 99999-9999"
                                                value={formData.phone}
                                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                                className="pl-10 bg-white"
                                                required
                                                data-testid="register-phone-google"
                                            />
                                        </div>
                                    </div>
                                )}
                                
                                <div className="space-y-2">
                                    <Label htmlFor="cep">CEP *</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            id="cep"
                                            placeholder="00000-000"
                                            value={formData.address.cep}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, '');
                                                setFormData({ 
                                                    ...formData, 
                                                    address: { ...formData.address, cep: value }
                                                });
                                                if (value.length === 8) {
                                                    handleCepLookup(value);
                                                }
                                            }}
                                            maxLength={8}
                                            className="bg-white"
                                            data-testid="register-cep"
                                        />
                                        <Button 
                                            type="button" 
                                            variant="outline"
                                            onClick={() => handleCepLookup(formData.address.cep)}
                                            disabled={loadingCep}
                                        >
                                            {loadingCep ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
                                        </Button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="street">Rua *</Label>
                                    <Input
                                        id="street"
                                        placeholder="Nome da rua"
                                        value={formData.address.street}
                                        onChange={(e) => setFormData({ 
                                            ...formData, 
                                            address: { ...formData.address, street: e.target.value }
                                        })}
                                        className="bg-white"
                                        data-testid="register-street"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="number">Número *</Label>
                                        <Input
                                            id="number"
                                            placeholder="123"
                                            value={formData.address.number}
                                            onChange={(e) => setFormData({ 
                                                ...formData, 
                                                address: { ...formData.address, number: e.target.value }
                                            })}
                                            className="bg-white"
                                            data-testid="register-number"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="complement">Complemento</Label>
                                        <Input
                                            id="complement"
                                            placeholder="Apto, Bloco..."
                                            value={formData.address.complement}
                                            onChange={(e) => setFormData({ 
                                                ...formData, 
                                                address: { ...formData.address, complement: e.target.value }
                                            })}
                                            className="bg-white"
                                            data-testid="register-complement"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="neighborhood">Bairro *</Label>
                                    <Input
                                        id="neighborhood"
                                        placeholder="Bairro"
                                        value={formData.address.neighborhood}
                                        onChange={(e) => setFormData({ 
                                            ...formData, 
                                            address: { ...formData.address, neighborhood: e.target.value }
                                        })}
                                        className="bg-white"
                                        data-testid="register-neighborhood"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="city">Cidade *</Label>
                                        <Input
                                            id="city"
                                            placeholder="Cidade"
                                            value={formData.address.city}
                                            onChange={(e) => setFormData({ 
                                                ...formData, 
                                                address: { ...formData.address, city: e.target.value }
                                            })}
                                            className="bg-white"
                                            data-testid="register-city"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="state">Estado *</Label>
                                        <Select
                                            value={formData.address.state}
                                            onValueChange={(value) => setFormData({ 
                                                ...formData, 
                                                address: { ...formData.address, state: value }
                                            })}
                                        >
                                            <SelectTrigger className="bg-white" data-testid="register-state">
                                                <SelectValue placeholder="UF" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {STATES.map(state => (
                                                    <SelectItem key={state} value={state}>{state}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {/* Emergency Contact */}
                                <div className="pt-4 border-t border-slate-200">
                                    <h3 className="font-semibold text-slate-800 mb-3">Contato de Emergência</h3>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label>Nome do contato</Label>
                                            <Input
                                                placeholder="Nome completo"
                                                value={formData.emergency_contact.name}
                                                onChange={(e) => setFormData({ 
                                                    ...formData, 
                                                    emergency_contact: { ...formData.emergency_contact, name: e.target.value }
                                                })}
                                                className="bg-white"
                                                data-testid="emergency-name"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Telefone</Label>
                                                <Input
                                                    placeholder="(11) 99999-9999"
                                                    value={formData.emergency_contact.phone}
                                                    onChange={(e) => setFormData({ 
                                                        ...formData, 
                                                        emergency_contact: { ...formData.emergency_contact, phone: e.target.value }
                                                    })}
                                                    className="bg-white"
                                                    data-testid="emergency-phone"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Parentesco</Label>
                                                <Select
                                                    value={formData.emergency_contact.relationship}
                                                    onValueChange={(value) => setFormData({ 
                                                        ...formData, 
                                                        emergency_contact: { ...formData.emergency_contact, relationship: value }
                                                    })}
                                                >
                                                    <SelectTrigger className="bg-white" data-testid="emergency-relationship">
                                                        <SelectValue placeholder="Selecione" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="conjuge">Cônjuge</SelectItem>
                                                        <SelectItem value="pai_mae">Pai/Mãe</SelectItem>
                                                        <SelectItem value="filho">Filho(a)</SelectItem>
                                                        <SelectItem value="irmao">Irmão(ã)</SelectItem>
                                                        <SelectItem value="amigo">Amigo(a)</SelectItem>
                                                        <SelectItem value="outro">Outro</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-3 pt-2">
                                    <Button type="button" variant="outline" onClick={() => isGoogleProvider ? setStep(1) : prevStep()} className="flex-1">
                                        <ChevronLeft className="w-4 h-4 mr-2" />
                                        Voltar
                                    </Button>
                                    {isProvider ? (
                                        <Button type="button" onClick={nextStep} className="flex-1 btn-primary">
                                            Continuar
                                            <ChevronRight className="w-4 h-4 ml-2" />
                                        </Button>
                                    ) : (
                                        <Button type="button" onClick={nextStep} className="flex-1 btn-primary">
                                            Finalizar
                                            <ChevronRight className="w-4 h-4 ml-2" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Step 4: Provider Info (only for providers) */}
                        {step === 4 && isProvider && (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="cpf">CPF *</Label>
                                    <div className="relative">
                                        <Input
                                            id="cpf"
                                            placeholder="000.000.000-00"
                                            value={formData.cpf}
                                            onChange={(e) => handleCpfChange(e.target.value)}
                                            className={`bg-white pr-10 ${
                                                cpfError ? 'border-red-500 focus-visible:ring-red-500' : 
                                                cpfValid ? 'border-green-500 focus-visible:ring-green-500' : ''
                                            }`}
                                            maxLength={14}
                                            data-testid="register-cpf"
                                        />
                                        {formData.cpf.length > 0 && (
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                {cpfValid ? (
                                                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                                                ) : cpfError ? (
                                                    <AlertCircle className="w-5 h-5 text-red-500" />
                                                ) : null}
                                            </div>
                                        )}
                                    </div>
                                    {cpfError && (
                                        <p className="text-sm text-red-500 flex items-center gap-1">
                                            <AlertCircle className="w-4 h-4" />
                                            {cpfError}
                                        </p>
                                    )}
                                    {cpfValid && (
                                        <p className="text-sm text-green-600 flex items-center gap-1">
                                            <CheckCircle2 className="w-4 h-4" />
                                            CPF válido
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="identity">Documento de Identidade (RG ou CNH)</Label>
                                    <Input
                                        id="identity"
                                        placeholder="Número do documento"
                                        value={formData.identity_document}
                                        onChange={(e) => setFormData({ ...formData, identity_document: e.target.value })}
                                        className="bg-white"
                                        data-testid="register-identity"
                                    />
                                    <p className="text-xs text-slate-500">
                                        Você precisará enviar uma foto do documento após o cadastro
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label>Serviços que deseja oferecer</Label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {Object.entries(SERVICE_TYPES).filter(([key]) => key !== 'others').map(([key, { label }]) => (
                                            <label
                                                key={key}
                                                className={`flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                                    formData.services_offered?.includes(key)
                                                        ? 'border-green-500 bg-green-50'
                                                        : 'border-slate-200 hover:border-slate-300 bg-white'
                                                } ${key === 'partner' ? 'col-span-2' : ''}`}
                                            >
                                                <Checkbox
                                                    checked={formData.services_offered?.includes(key)}
                                                    onCheckedChange={() => toggleService(key)}
                                                />
                                                <span className="text-sm">{label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="experience">Experiência com pets *</Label>
                                    <Textarea
                                        id="experience"
                                        placeholder="Conte sobre sua experiência cuidando de animais, certificações, cursos, etc."
                                        value={formData.experience_description}
                                        onChange={(e) => setFormData({ ...formData, experience_description: e.target.value })}
                                        rows={4}
                                        className="bg-white"
                                        data-testid="register-experience"
                                    />
                                </div>

                                {/* PIX and Bank Info Section */}
                                <div className="pt-4 border-t">
                                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                        💰 Dados para Recebimento (Obrigatório)
                                    </h3>
                                    <p className="text-xs text-slate-500 mb-4">
                                        Informe seus dados bancários para receber os pagamentos das reservas.
                                    </p>
                                    
                                    <div className="space-y-3">
                                        {/* PIX Key Type */}
                                        <div>
                                            <Label className="text-sm">Tipo de Chave PIX *</Label>
                                            <Select 
                                                value={formData.pix_key_type} 
                                                onValueChange={(value) => setFormData({ ...formData, pix_key_type: value, pix_key: '' })}
                                            >
                                                <SelectTrigger className="bg-white" data-testid="pix-key-type">
                                                    <SelectValue placeholder="Selecione o tipo" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="cpf">CPF</SelectItem>
                                                    <SelectItem value="cnpj">CNPJ</SelectItem>
                                                    <SelectItem value="email">E-mail</SelectItem>
                                                    <SelectItem value="phone">Telefone</SelectItem>
                                                    <SelectItem value="random">Chave Aleatória</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* PIX Key */}
                                        <div>
                                            <Label className="text-sm">Chave PIX *</Label>
                                            <Input
                                                placeholder={
                                                    formData.pix_key_type === 'cpf' ? '000.000.000-00' :
                                                    formData.pix_key_type === 'cnpj' ? '00.000.000/0000-00' :
                                                    formData.pix_key_type === 'email' ? 'seu@email.com' :
                                                    formData.pix_key_type === 'phone' ? '(11) 99999-9999' :
                                                    'Cole sua chave aleatória'
                                                }
                                                value={formData.pix_key}
                                                onChange={(e) => setFormData({ ...formData, pix_key: e.target.value })}
                                                className="bg-white"
                                                data-testid="pix-key"
                                            />
                                        </div>

                                        {/* Account Holder Name */}
                                        <div>
                                            <Label className="text-sm">Nome do Titular da Conta *</Label>
                                            <Input
                                                placeholder="Nome completo conforme conta bancária"
                                                value={formData.bank_account_holder}
                                                onChange={(e) => setFormData({ ...formData, bank_account_holder: e.target.value })}
                                                className="bg-white"
                                                data-testid="bank-holder"
                                            />
                                        </div>

                                        {/* Bank Name (optional) */}
                                        <div>
                                            <Label className="text-sm">Banco (opcional)</Label>
                                            <Input
                                                placeholder="Ex: Nubank, Itaú, Bradesco..."
                                                value={formData.bank_name}
                                                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                                                className="bg-white"
                                                data-testid="bank-name"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Profile Photo Section */}
                                <div className="pt-4 border-t">
                                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                        📷 Foto de Perfil (Obrigatório)
                                    </h3>
                                    <p className="text-xs text-slate-500 mb-4">
                                        Envie uma foto sua para que os clientes possam te identificar.
                                    </p>
                                    
                                    <div className="flex items-center gap-4">
                                        {/* Photo Preview */}
                                        <div className="w-24 h-24 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden">
                                            {formData.profile_photo_preview ? (
                                                <img 
                                                    src={formData.profile_photo_preview} 
                                                    alt="Preview" 
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <Camera className="w-8 h-8 text-slate-400" />
                                            )}
                                        </div>
                                        
                                        {/* Upload Button */}
                                        <div className="flex-1">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                id="profile-photo-upload"
                                                className="hidden"
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        if (file.size > 5 * 1024 * 1024) {
                                                            toast.error('A foto deve ter no máximo 5MB');
                                                            return;
                                                        }
                                                        const reader = new FileReader();
                                                        reader.onloadend = () => {
                                                            setFormData({
                                                                ...formData,
                                                                profile_photo: file,
                                                                profile_photo_preview: reader.result
                                                            });
                                                        };
                                                        reader.readAsDataURL(file);
                                                    }
                                                }}
                                                data-testid="profile-photo-input"
                                            />
                                            <label 
                                                htmlFor="profile-photo-upload"
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-[#4A90B2] text-white rounded-lg cursor-pointer hover:bg-[#3d7a99] transition-colors"
                                            >
                                                <Upload className="w-4 h-4" />
                                                {formData.profile_photo ? 'Trocar foto' : 'Enviar foto'}
                                            </label>
                                            {formData.profile_photo && (
                                                <p className="text-xs text-green-600 mt-2">✓ Foto selecionada</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-3 pt-2">
                                    <Button type="button" variant="outline" onClick={prevStep} className="flex-1">
                                        <ChevronLeft className="w-4 h-4 mr-2" />
                                        Voltar
                                    </Button>
                                    <Button type="button" onClick={nextStep} className="flex-1 btn-primary">
                                        Finalizar
                                        <ChevronRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Step 5: Vehicle Data (Taxi Dog providers only) */}
                        {step === 5 && wantsTaxiDog && (
                            <VehicleStep 
                                formData={formData} 
                                setFormData={setFormData}
                                onNext={nextStep}
                                onBack={prevStep}
                            />
                        )}


                        {/* Final Step: Terms - Shows after all other steps */}
                        {step === totalSteps + 1 && (
                            <div className="space-y-4">
                                {/* Trust messages */}
                                <div className="space-y-3">
                                    <div className="flex items-start gap-3 p-3 bg-[#F0F9FF] rounded-xl">
                                        <Shield className="w-5 h-5 text-[#4A90B2] flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-slate-700">
                                            Sua segurança e a do seu pet são nossa prioridade. Todos os prestadores 
                                            passam por verificação.
                                        </p>
                                    </div>
                                    <div className="flex items-start gap-3 p-3 bg-green-50 rounded-xl">
                                        <FileText className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-slate-700">
                                            Seus dados são protegidos conforme a LGPD (Lei Geral de Proteção de Dados).
                                        </p>
                                    </div>
                                </div>

                                {/* Terms checkbox */}
                                <div className={`flex items-start gap-3 p-4 border-2 rounded-xl transition-colors ${
                                    formData.accepted_terms 
                                        ? 'border-green-500 bg-green-50' 
                                        : 'border-slate-200 bg-white'
                                }`}>
                                    <Checkbox
                                        id="terms"
                                        checked={formData.accepted_terms}
                                        onCheckedChange={(checked) => setFormData({ ...formData, accepted_terms: checked })}
                                        data-testid="accept-terms"
                                    />
                                    <label htmlFor="terms" className="text-sm text-slate-700 cursor-pointer">
                                        Li e aceito os{' '}
                                        <Link to="/terms" target="_blank" className="text-[#4A90B2] hover:underline">
                                            Termos de Uso
                                        </Link>{' '}
                                        e a{' '}
                                        <Link to="/privacy" target="_blank" className="text-[#4A90B2] hover:underline">
                                            Política de Privacidade
                                        </Link>{' '}
                                        do RecepPet.
                                    </label>
                                </div>

                                <div className="flex gap-3 pt-2">
                                    <Button type="button" variant="outline" onClick={prevStep} className="flex-1">
                                        <ChevronLeft className="w-4 h-4 mr-2" />
                                        Voltar
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="flex-1 btn-primary"
                                        disabled={loading || !formData.accepted_terms}
                                        data-testid="register-submit"
                                    >
                                        {loading ? (
                                            <span className="flex items-center gap-2">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                <span className="text-sm">{loadingStage || 'Processando...'}</span>
                                            </span>
                                        ) : (
                                            'Criar conta'
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </form>
                    
                    <div className="mt-6 text-center text-sm">
                        <span className="text-slate-500">Já tem uma conta? </span>
                        <Link to="/login" className="text-[#4A90B2] font-medium hover:underline" data-testid="login-link">
                            Entrar
                        </Link>
                    </div>
                </CardContent>
            </Card>
            </div>

            {/* Modal de Sucesso - Cadastro enviado para aprovação */}
            <Dialog open={showSuccessModal} onOpenChange={() => {}}>
                <DialogContent className="max-w-md text-center p-8" onPointerDownOutside={(e) => e.preventDefault()}>
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle className="w-10 h-10 text-green-600" />
                        </div>
                        {successType === 'provider' ? (
                            <>
                                <h2 className="text-xl font-bold text-slate-900">Cadastro Enviado!</h2>
                                <p className="text-slate-600 text-sm leading-relaxed">
                                    Seus dados e documentos foram enviados com sucesso para analise do administrador.
                                    Voce sera notificado assim que seu cadastro for aprovado.
                                </p>
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg w-full">
                                    <p className="text-xs text-amber-700">
                                        O prazo medio de analise e de ate 24 horas. Enquanto isso, seu perfil ficara como "Pendente" no sistema.
                                    </p>
                                </div>
                            </>
                        ) : (
                            <>
                                <h2 className="text-xl font-bold text-slate-900">Cadastro Realizado!</h2>
                                <p className="text-slate-600 text-sm leading-relaxed">
                                    Seus dados foram salvos com sucesso. Agora, verifique seu e-mail e telefone para ativar sua conta.
                                    Apos a verificacao, seus documentos serao analisados pelo administrador.
                                </p>
                                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg w-full">
                                    <p className="text-xs text-blue-700">
                                        Enviamos um codigo de verificacao para seu e-mail e telefone.
                                    </p>
                                </div>
                            </>
                        )}
                        <Button 
                            className="w-full mt-2 bg-[#4A90B2] hover:bg-[#3a7a9c] text-white h-12 text-base"
                            onClick={() => navigate(successRedirect)}
                            data-testid="success-go-to-dashboard"
                        >
                            {successType === 'provider' ? 'Ir para Meu Painel' : 'Verificar Minha Conta'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
